/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2026 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

import { useEffect } from "react";

import * as React from "react";
import styled from "styled-components";
import { Heading22 } from "components/shared/Heading";
import { Text18 } from "components/shared/Text";
import chromeIllustration from "images/chrome-illustration.svg";
import { devices } from "components/shared/Styles";
import Analytics, { AnalyticsEvent, NewRelicEvent } from "constants/Analytics";
import DesktopEnforcementPage from "components/DesktopEnforcementPage";
import { BROWSER_ENFORCEMENT_Z_INDEX } from "components/Studio/ProjectContent/constants";

const Background = styled.div`
  position: fixed;
  left: 0;
  right: 0;
  top: 0;
  background-color: var(--spectrum-semantic-cta-background-color-default);
  display: flex;
  justify-content: center;
  overflow: scroll;
  height: 100%;
  z-index: ${BROWSER_ENFORCEMENT_Z_INDEX};
`;

const Content = styled.div`
  position: relative;
  top: 200px;
  height: 500px;
  width: 360px;
  border-radius: 16px;
  overflow: hidden;
  background-color: var(--spectrum-global-color-static-gray-300);

  ${devices.small} {
    top: 100px;
  }
`;

const Illustration = styled.div`
  position: absolute;
  top: 0px;
  height: 265px;
  width: 100%;
  background-image: url("${chromeIllustration}");
  background-color: var(--spectrum-global-color-static-gray-300);
  background-position: center;
  background-repeat: no-repeat;
`;

const MessageContainer = styled.div`
  position: absolute;
  bottom: 0px;
  height: 243px;
  width: 100%;
  background-color: var(--spectrum-global-color-static-white);
`;

const Message = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  margin: 0 50px;
`;

const BrowserEnforcementPage: React.FC = () => {
  useEffect(() => {
    Analytics.instance.logEvent(AnalyticsEvent.BROWSER_ENFORCEMENT_PAGE_VIEWED);
    newrelic.addPageAction(NewRelicEvent.BROWSER_ENFORCEMENT_PAGE_VIEWED);
  }, []);

  return (
    <Background>
      <Content>
        <Illustration data-testid="chrome-illustration" />
        <MessageContainer>
          <Message>
            <Heading22>{I18n.t("browser_enforcement.heading")}</Heading22>
            <Text18
              dangerouslySetInnerHTML={{
                __html: (I18n.t("browser_enforcement.body") ?? "") as string,
              }}
            />
          </Message>
        </MessageContainer>
      </Content>
    </Background>
  );
};

export default BrowserEnforcementPage;
